/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LoadingService, LogService, NavigationService } from '../../../../../../core/services';
import {
    EnduranceRBACGroupsApiService,
    EnduranceRBACRolesApiService,
    EnduranceRBACUsersApiService
} from '../../../../shared/http';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-groups',
    templateUrl: './endurance-add-edit-groups.component.html'
})
export class EnduranceAddEditGroupsComponent extends BaseComponent {
    /**
     * Api data for RBAC groups
     */
    public data: any;

    constructor(
        private activatedRoute: ActivatedRoute,
        private enduranceRBACGroupsApiService: EnduranceRBACGroupsApiService,
        private enduranceRBACRolesApiService: EnduranceRBACRolesApiService,
        private enduranceRBACUsersApiService: EnduranceRBACUsersApiService,
        private loadingService: LoadingService,
        private logService: LogService,
        private navigateService: NavigationService,
        private router: Router
    ) {
        super();

        const id: string = this.activatedRoute.snapshot.paramMap.get('id');
        this.setUpPageData(id);
    }

    /**
     * Submit role.
     * @param groupValues
     */
    public submit(groupValues: any): void {
        const apiCall = groupValues.id
            ? this.enduranceRBACGroupsApiService.edit(groupValues)
            : this.enduranceRBACGroupsApiService.add(groupValues);

        apiCall
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.loadingService.loadingButtonEmitter.next(false);
                    const url: string = this.navigateService.getEnduranceUrl('/maintenance/users');
                    this.router.navigate([url]).then().catch();
                },
                error: (error: ErrorResponseModel): void => {
                    this.loadingService.loadingButtonEmitter.next(false);
                    this.logService.error('EnduranceAddEditGroupsComponent: Submit', error.message);
                }
            });
    }

    /**
     * Set up page data for edit or duplicate.
     * @param id
     * @private
     */
    private setUpPageData(id: string): void {
        const apiCalls = {
            users: this.enduranceRBACUsersApiService.getAllUsers(),
            roles: this.enduranceRBACRolesApiService.getAllRoles(),
            groups: this.enduranceRBACGroupsApiService.getAllGroups(),
        };

        if (id) {
            apiCalls['groupPatch'] = this.enduranceRBACGroupsApiService.getGroupById(id?.toString());
        }

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    this.data = dataGroups;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EnduranceAddEditGroupsComponent: setUpPageData', error.message);
                }
            });
    }
}
